/**
 * Hot Leads dashboard page — surfaces returning visitors flagged by the
 * backend hot_leads aggregation (session_id with ≥3 view events).
 *
 * Column order matches broker mental model: WHO (ip/email) → WHAT (domains
 * viewed) → HOW OFTEN (views + revisits) → LAST TOUCH (last_seen + referrer).
 * A "hottest" table sits above; a longer paginated view sits below.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Clock, Globe, Copy, RefreshCcw, ChevronDown, ChevronRight, Zap, Building2, Mail } from "lucide-react";
import { toast } from "sonner";
import Sidebar from "@/components/dashboard/Sidebar";
import { api } from "@/lib/api";

function timeAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.floor((now - then) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function HotLeads() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [minViews, setMinViews] = useState(3);
  const [bullseyeMeta, setBullseyeMeta] = useState({ configured: false, matched_sessions: 0 });
  const [groupByCompany, setGroupByCompany] = useState(false);
  const [icpOnly, setIcpOnly] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/hot-leads", { params: { days, min_views: minViews } })
      .then((r) => {
        setRows(r.data.items || []);
        setBullseyeMeta(r.data.bullseye || { configured: false, matched_sessions: 0 });
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [days, minViews]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]" data-testid="hot-leads-page">
      <Sidebar />
      <main className="flex-1 p-8 max-w-[1400px]">
        <header className="mb-8 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="overline flex items-center gap-2" style={{ color: "var(--cd-danger)" }}>
              <Flame className="w-3.5 h-3.5" strokeWidth={2} /> Hot Leads
            </div>
            <h1 className="font-display text-4xl tracking-tight mt-2" data-testid="hot-leads-title">
              Visitors coming back for more.
            </h1>
            <p className="text-sm text-[#475467] mt-2 max-w-xl">
              Any single session that has viewed <strong>{minViews}+ domains</strong> in the last {days} days. Focus here first — these buyers are the closest to writing you a cheque.
            </p>
            {bullseyeMeta.configured && (
              <div
                className="inline-flex items-center gap-2 mt-3 px-2.5 py-1 rounded-md text-[11px] font-semibold"
                style={{ background: "#e0f2fe", color: "#075985" }}
                data-testid="bullseye-enrichment-pill"
                title="Bullseye is actively matching visitor IPs to identified companies. Rows below get a company chip when Bullseye recognizes the network."
              >
                <Building2 size={11} strokeWidth={2} />
                Bullseye enrichment active · {bullseyeMeta.matched_sessions}/{rows.length} sessions identified
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              data-testid="hot-leads-days"
              className="cd-input"
              style={{ width: 130, height: 40 }}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 12 months</option>
            </select>
            <select
              value={minViews}
              onChange={(e) => setMinViews(Number(e.target.value))}
              data-testid="hot-leads-threshold"
              className="cd-input"
              style={{ width: 130, height: 40 }}
            >
              <option value={2}>≥ 2 views</option>
              <option value={3}>≥ 3 views</option>
              <option value={5}>≥ 5 views</option>
              <option value={10}>≥ 10 views</option>
            </select>
            <button onClick={load} className="cd-btn cd-btn--outline cd-btn--sm" data-testid="hot-leads-refresh">
              <RefreshCcw size={13} strokeWidth={2} /> Refresh
            </button>
          </div>
        </header>

        {bullseyeMeta.configured && bullseyeMeta.matched_sessions > 0 && (
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <button
              onClick={() => setGroupByCompany((v) => !v)}
              className={`cd-btn cd-btn--sm ${groupByCompany ? "cd-btn--primary" : "cd-btn--outline"}`}
              data-testid="hot-leads-group-toggle"
              title="Merge every session identified as the same Bullseye company into one row so you see the whole buying committee at a glance."
            >
              <Building2 size={12} strokeWidth={2} />
              {groupByCompany ? "Grouped by company" : "Group by company"}
            </button>
            <button
              onClick={() => setIcpOnly((v) => !v)}
              className={`cd-btn cd-btn--sm ${icpOnly ? "cd-btn--primary" : "cd-btn--outline"}`}
              data-testid="hot-leads-icp-toggle"
              title="Hide sessions from companies Bullseye has NOT flagged as an Ideal Customer Profile match. Focus on the buyers most likely to close."
              style={icpOnly ? { background: "#065f46", borderColor: "#065f46" } : undefined}
            >
              <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: ".05em" }}>ICP</span>
              {icpOnly ? "ICP only" : "Filter to ICP"}
            </button>
            <span className="text-[11px] text-[#98a2b3]">
              {icpOnly
                ? "Showing only sessions Bullseye flagged as an Ideal Customer Profile."
                : "Roll up all sessions from the same identified company into one row."}
            </span>
          </div>
        )}

        {loading ? (
          <div className="grid-panel p-12 text-center text-sm text-[#98a2b3]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="grid-panel p-16 text-center" data-testid="hot-leads-empty">
            <Flame className="w-8 h-8 mx-auto text-[#98a2b3] mb-4" strokeWidth={1.5} />
            <div className="font-display text-xl mb-2">No hot leads yet.</div>
            <p className="text-sm text-[#475467] max-w-md mx-auto">
              As soon as a visitor loads {minViews} or more landers within {days} days, they'll appear here.
            </p>
          </div>
        ) : (
          (() => {
            const displayRows = icpOnly
              ? rows.filter((r) => r.bullseye_company && r.bullseye_company.icp)
              : rows;
            if (icpOnly && displayRows.length === 0) {
              return (
                <div className="grid-panel p-16 text-center" data-testid="hot-leads-empty-icp">
                  <Building2 className="w-8 h-8 mx-auto text-[#98a2b3] mb-4" strokeWidth={1.5} />
                  <div className="font-display text-xl mb-2">No ICP sessions in this window.</div>
                  <p className="text-sm text-[#475467] max-w-md mx-auto">
                    None of the {rows.length} recent sessions match a company Bullseye flagged as an Ideal Customer Profile. Turn the filter off to see everyone.
                  </p>
                </div>
              );
            }
            return groupByCompany ? (
              <CompanyGroupedTable rows={displayRows} />
            ) : (
              <div className="grid-panel">
                <table className="w-full text-sm" data-testid="hot-leads-table">
                  <thead>
                    <tr className="text-left">
                      <th style={th}>Session · Location</th>
                      <th style={th}>Domains viewed</th>
                      <th style={th}>Views</th>
                      <th style={th}>First seen</th>
                      <th style={th}>Last seen</th>
                      <th style={th}>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayRows.map((r) => <HotLeadRow key={r.session_id} r={r} />)}
                  </tbody>
                </table>
              </div>
            );
          })()
        )}

        <BullseyeCompaniesSection />
      </main>
    </div>
  );
}

const th = {
  padding: "12px 16px",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  fontWeight: 700,
  color: "var(--cd-gray-500)",
  borderBottom: "1px solid var(--cd-gray-200)",
  background: "#fafbfc",
};
const td = { padding: "14px 16px", verticalAlign: "top", borderBottom: "1px solid var(--cd-gray-200)" };


function BullseyeCompanyChip({ c, prominent = false }) {
  const site = (c.domain || "").replace(/^https?:\/\//i, "").replace(/\/$/, "");
  if (prominent) {
    return (
      <div
        className="inline-flex items-center gap-2"
        data-testid={`bullseye-company-${c.id || c.name}`}
      >
        {c.logo_url ? (
          <img
            src={c.logo_url}
            alt=""
            className="w-6 h-6 rounded object-contain bg-white border border-[#eaecf0]"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="w-6 h-6 rounded bg-[#e0f2fe] flex items-center justify-center text-[9px] font-semibold" style={{ color: "#075985" }}>
            {(c.name || "?").slice(0, 2).toUpperCase()}
          </div>
        )}
        <div>
          <div className="text-xs font-semibold text-[#101828]">
            {c.name || site || "Unknown company"}
            {c.icp && (
              <span
                className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold"
                style={{ background: "#ecfdf5", color: "#065f46" }}
                title="Bullseye flagged this company as an Ideal Customer Profile match."
              >
                ICP
              </span>
            )}
          </div>
          {site && <div className="text-[10px] text-[#475467]">{site}</div>}
        </div>
      </div>
    );
  }
  return (
    <div
      className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold"
      style={{ background: "#e0f2fe", color: "#075985" }}
      title={`Bullseye identified this network as ${c.name || site || "an unknown company"}${c.industry ? ` · ${c.industry}` : ""}`}
      data-testid={`bullseye-company-${c.id || c.name}`}
    >
      <Building2 size={9} strokeWidth={2} />
      {c.name || site || "Company match"}
      {c.icp && <span className="opacity-70 font-bold">· ICP</span>}
    </div>
  );
}

function HotLeadRow({ r }) {
  const [expanded, setExpanded] = useState(false);
  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copied");
  };
  const timeline = r.timeline || [];
  const hasTimeline = timeline.length > 0;
  return (
    <>
    <tr data-testid={`hot-lead-row-${r.session_id}`}>
      <td style={td}>
        {hasTimeline ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1.5 font-mono text-xs text-[#101828] font-medium hover:text-[#03142f]"
            data-testid={`toggle-timeline-${r.session_id}`}
            aria-expanded={expanded}
          >
            {expanded ? <ChevronDown size={12} strokeWidth={2} /> : <ChevronRight size={12} strokeWidth={2} />}
            {r.session_id.slice(0, 12)}…
          </button>
        ) : (
          <div className="font-mono text-xs text-[#101828] font-medium">{r.session_id.slice(0, 12)}…</div>
        )}
        <div
          className="text-[11px] text-[#98a2b3] flex items-center gap-1 mt-1"
          title={r.ip || ""}
        >
          {r.geo && (r.geo.city || r.geo.country) ? (
            <>
              {r.geo.city && <span className="text-[#101828] font-medium">{r.geo.city},</span>}
              <span>{r.geo.country || r.geo.region}</span>
            </>
          ) : (
            <span className="font-mono">{r.ip || "—"}</span>
          )}
          {r.ip && (
            <button onClick={() => copy(r.ip)} className="hover:text-[#101828]" data-testid={`copy-ip-${r.session_id}`} aria-label="Copy IP">
              <Copy size={10} strokeWidth={2} />
            </button>
          )}
        </div>
      </td>
      <td style={td}>
        <div className="flex flex-wrap gap-1.5">
          {(r.domains || []).slice(0, 6).map((d) => (
            <Link
              key={d}
              to={`/l/${d}`}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium"
              style={{ background: "var(--cd-cyan-100)", color: "var(--cd-navy-950)" }}
            >
              <Globe size={10} strokeWidth={2} />{d}
            </Link>
          ))}
          {r.domains.length > 6 && (
            <span className="text-[11px] text-[#98a2b3] px-2 py-1">+{r.domains.length - 6}</span>
          )}
        </div>
      </td>
      <td style={td}>
        <div className="flex items-center gap-1.5">
          <span
            className="inline-flex items-center justify-center rounded-full font-bold text-white text-xs"
            style={{
              minWidth: 28, height: 28, padding: "0 8px",
              background: r.views >= 10 ? "#dc2626" : r.views >= 5 ? "#ea580c" : "var(--cd-navy-950)",
            }}
          >
            {r.views}
          </span>
          <span className="text-[11px] text-[#98a2b3]">across {r.unique_domains} domain{r.unique_domains === 1 ? "" : "s"}</span>
        </div>
        {r.peak_alert && (
          <div
            className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-sm"
            style={{ background: "#FEF3C7", color: "#92400E" }}
            title={`Alert fired ${r.peak_alert.email_sent ? "via email " : ""}when session crossed ${r.peak_alert.threshold} views on ${r.peak_alert.domain}`}
            data-testid={`peak-alert-${r.session_id}`}
          >
            <Zap size={10} strokeWidth={2} />
            Peak alert · {r.peak_alert.domain} · {timeAgo(r.peak_alert.sent_at)}
            {r.peak_alert.email_sent && <span className="opacity-60">· ✉ sent</span>}
            {r.peak_alert.total_alerts > 1 && <span className="opacity-60">· +{r.peak_alert.total_alerts - 1} more</span>}
          </div>
        )}
      </td>
      <td style={td}>
        <div className="text-xs text-[#101828]">{timeAgo(r.first_seen)}</div>
      </td>
      <td style={td}>
        <div className="text-xs text-[#101828]">{timeAgo(r.last_seen)}</div>
        <div className="text-[10px] text-[#98a2b3] mt-1 flex items-center gap-1">
          <Clock size={9} strokeWidth={2} />
          {r.last_seen ? new Date(r.last_seen).toLocaleString() : ""}
        </div>
      </td>
      <td style={td}>
        {r.email ? (
          <div>
            <a href={`mailto:${r.email}`} className="text-xs" style={{ color: "var(--cd-blue-500)", fontWeight: 600 }}>
              {r.email}
            </a>
            {r.name && (
              <div className="text-[10px] text-[#98a2b3] mt-1">
                {r.name}{r.identified_via ? ` · via ${r.identified_via}` : ""}
              </div>
            )}
            {r.bullseye_company && <BullseyeCompanyChip c={r.bullseye_company} />}
          </div>
        ) : r.bullseye_company ? (
          <div>
            <BullseyeCompanyChip c={r.bullseye_company} prominent />
            <div className="text-[10px] text-[#98a2b3] mt-1 italic">no email · identified via Bullseye</div>
          </div>
        ) : (
          <span className="text-[11px] text-[#98a2b3] italic">not identified</span>
        )}
      </td>
    </tr>
    {expanded && hasTimeline && (
      <tr data-testid={`timeline-${r.session_id}`}>
        <td colSpan={6} style={{ ...td, background: "#fafbfc", paddingTop: 8, paddingBottom: 20 }}>
          <AttributionTimeline events={timeline} peakDomain={findPeakDomain(timeline)} />
        </td>
      </tr>
    )}
    </>
  );
}

/** Compact horizontal timeline of view events. Dots scale with recency,
 *  the peak-intent moment (most-repeated domain) gets a labelled pin.
 */
function AttributionTimeline({ events, peakDomain }) {
  if (!events.length) return null;
  const first = new Date(events[0].at).getTime();
  const last = new Date(events[events.length - 1].at).getTime();
  const span = Math.max(1, last - first);
  return (
    <div className="pl-6 pr-4">
      <div className="text-[10px] uppercase tracking-widest text-[#98a2b3] font-mono mb-3 flex items-center justify-between">
        <span>Attribution timeline · {events.length} views</span>
        {peakDomain && (
          <span className="text-[#101828] font-semibold normal-case tracking-normal">
            Peak intent: <span style={{ color: "var(--cd-blue-500)" }}>{peakDomain.domain}</span> · {peakDomain.count} views
          </span>
        )}
      </div>
      <div className="relative" style={{ height: 80 }}>
        {/* baseline */}
        <div className="absolute left-0 right-0" style={{ top: 40, height: 2, background: "var(--cd-gray-200)" }} />
        {events.map((ev, i) => {
          const t = new Date(ev.at).getTime();
          const pctX = span > 0 ? ((t - first) / span) * 100 : 0;
          const isPeak = peakDomain && ev.domain === peakDomain.domain;
          const color = isPeak ? "#dc2626" : "var(--cd-navy-950)";
          const size = isPeak ? 14 : 10;
          return (
            <div
              key={i}
              className="absolute"
              style={{ left: `${pctX}%`, top: 40 - size / 2, transform: "translateX(-50%)" }}
              title={`${ev.domain} · ${new Date(ev.at).toLocaleString()}${ev.referrer ? " · from " + ev.referrer : ""}`}
              data-testid={`timeline-dot-${i}`}
            >
              <div
                style={{
                  width: size, height: size, borderRadius: "50%",
                  background: color, border: "2px solid #fff",
                  boxShadow: "0 0 0 1px " + color,
                }}
              />
              {(i === 0 || i === events.length - 1 || isPeak) && (
                <div
                  className="font-mono text-[9px]"
                  style={{
                    position: "absolute", top: isPeak ? -22 : 14,
                    left: "50%", transform: "translateX(-50%)",
                    whiteSpace: "nowrap", color: isPeak ? "#dc2626" : "#98a2b3",
                    fontWeight: isPeak ? 700 : 400,
                  }}
                >
                  {isPeak ? `↑ ${ev.domain}` : new Date(ev.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="text-[10px] text-[#98a2b3] font-mono flex justify-between mt-2">
        <span>{new Date(first).toLocaleString()}</span>
        <span>{new Date(last).toLocaleString()}</span>
      </div>
    </div>
  );
}

function findPeakDomain(events) {
  const counts = {};
  for (const e of events) {
    if (!e.domain) continue;
    counts[e.domain] = (counts[e.domain] || 0) + 1;
  }
  const entries = Object.entries(counts);
  if (!entries.length) return null;
  entries.sort((a, b) => b[1] - a[1]);
  const [domain, count] = entries[0];
  return count >= 2 ? { domain, count } : null;
}


// ============================================================================
// Bullseye.so identified-companies feed
// ============================================================================


// ============================================================================
// Company-grouped view — one row per identified Bullseye company, with all
// their sessions collapsed into an expandable timeline underneath.
// ============================================================================

function CompanyGroupedTable({ rows }) {
  // Bucket sessions by bullseye_company.id. Anything without a company match
  // gets grouped into a synthetic "Unidentified" bucket at the bottom so
  // brokers still see the pipeline while filtering for identified traffic.
  const groups = new Map();
  for (const r of rows) {
    const c = r.bullseye_company;
    const key = c && c.id ? `co:${c.id}` : "unidentified";
    if (!groups.has(key)) {
      groups.set(key, { company: c || null, sessions: [], key });
    }
    groups.get(key).sessions.push(r);
  }

  // Sort companies by total sessions desc, keep "unidentified" last.
  const ordered = [...groups.values()].sort((a, b) => {
    if (a.key === "unidentified") return 1;
    if (b.key === "unidentified") return -1;
    return b.sessions.length - a.sessions.length;
  });

  return (
    <div className="grid-panel" data-testid="hot-leads-grouped-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left">
            <th style={th}>Company</th>
            <th style={th}>Sessions</th>
            <th style={th}>Total views</th>
            <th style={th}>Domains touched</th>
            <th style={th}>Last seen</th>
            <th style={th}>Outreach</th>
          </tr>
        </thead>
        <tbody>
          {ordered.map((g) => <CompanyGroupRow key={g.key} group={g} />)}
        </tbody>
      </table>
    </div>
  );
}

function CompanyGroupRow({ group }) {
  const [expanded, setExpanded] = useState(false);
  const { company, sessions } = group;
  const totalViews = sessions.reduce((s, r) => s + (r.views || 0), 0);
  const domains = new Set();
  sessions.forEach((s) => (s.domains || []).forEach((d) => domains.add(d)));
  const lastSeen = sessions.reduce((max, r) => (r.last_seen > max ? r.last_seen : max), "");
  const site = company ? (company.domain || "").replace(/^https?:\/\//i, "").replace(/\/$/, "") : "";

  // Every session in the group shares the same company, so pull the
  // outreach summary off the first row that has it.
  const outreachList = sessions.find((s) => s.company_outreach)?.company_outreach || null;
  const outreachSummary = outreachList && outreachList.length > 0 ? {
    total: outreachList.length,
    sent: outreachList.filter((o) => o.status === "sent" || o.status === "replied").length,
    replied: outreachList.filter((o) => o.status === "replied").length,
    replyCount: outreachList.reduce((n, o) => n + (o.reply_count || 0), 0),
    lastAt: outreachList[0]?.created_at,
    lastSentiment: outreachList.find((o) => o.last_reply_sentiment)?.last_reply_sentiment || null,
  } : null;

  // "Send follow-up" nudge: latest outreach was sent 5+ days ago and
  // hasn't gotten a reply yet. Encourages persistence without spamming.
  const latest = outreachList && outreachList.length > 0 ? outreachList[0] : null;
  const daysSinceSent = latest && latest.sent_at
    ? Math.floor((Date.now() - new Date(latest.sent_at).getTime()) / 86400000)
    : null;
  const showFollowUp = latest && latest.status === "sent" && daysSinceSent !== null && daysSinceSent >= 5;

  return (
    <>
      <tr
        className="border-t border-[#eaecf0] hover:bg-[#fafafa] cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
        data-testid={`company-group-${group.key}`}
      >
        <td style={td}>
          <button className="inline-flex items-center gap-2 font-medium text-[#101828]">
            {expanded ? <ChevronDown size={12} strokeWidth={2} /> : <ChevronRight size={12} strokeWidth={2} />}
            {company ? (
              <span className="inline-flex items-center gap-2">
                {company.logo_url ? (
                  <img
                    src={company.logo_url}
                    alt=""
                    className="w-6 h-6 rounded object-contain bg-white border border-[#eaecf0]"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <span
                    className="w-6 h-6 rounded flex items-center justify-center text-[9px] font-semibold"
                    style={{ background: "#e0f2fe", color: "#075985" }}
                  >
                    {(company.name || "?").slice(0, 2).toUpperCase()}
                  </span>
                )}
                <span>
                  {company.name || site || "Unknown"}
                  {company.icp && (
                    <span
                      className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold"
                      style={{ background: "#ecfdf5", color: "#065f46" }}
                    >
                      ICP
                    </span>
                  )}
                </span>
              </span>
            ) : (
              <span className="text-[#98a2b3] italic">Unidentified sessions</span>
            )}
          </button>
          {site && (
            <div className="text-[11px] text-[#0369a1] mt-1 ml-6">{site}</div>
          )}
        </td>
        <td style={td}>
          <span className="font-semibold text-[#101828]">{sessions.length}</span>
        </td>
        <td style={td}>
          <span className="font-semibold text-[#101828]">{totalViews}</span>
        </td>
        <td style={td}>
          <div className="flex flex-wrap gap-1">
            {[...domains].slice(0, 4).map((d) => (
              <span
                key={d}
                className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium"
                style={{ background: "var(--cd-cyan-100)", color: "var(--cd-navy-950)" }}
              >
                {d}
              </span>
            ))}
            {domains.size > 4 && (
              <span className="text-[10px] text-[#98a2b3]">+{domains.size - 4}</span>
            )}
          </div>
        </td>
        <td style={td} className="text-[#475467]">{lastSeen ? timeAgo(lastSeen) : "—"}</td>
        <td style={td} onClick={(e) => e.stopPropagation()}>
          {company ? (
            <div className="flex flex-col gap-1.5 items-start">
              <button
                onClick={async () => {
                  // Log the outreach attempt server-side so we can badge the
                  // row with "1 drafted" — deliberate fire-and-forget; UI
                  // still opens Mail even if the log call fails.
                  try {
                    await api.post("/company-outreach", {
                      company_id: company.id,
                      company_name: company.name || site,
                      company_domain: site,
                      domains: [...domains],
                    });
                  } catch {}
                  const url = `/mail?compose=1&company=${encodeURIComponent(company.name || site)}&domain=${encodeURIComponent(site)}&domains=${encodeURIComponent([...domains].join(","))}&company_id=${encodeURIComponent(company.id)}`;
                  window.location.href = url;
                }}
                className="cd-btn cd-btn--outline cd-btn--sm"
                data-testid={`draft-email-${group.key}`}
                title={`Draft a cold email for ${company.name || site} using the domains they've viewed as talking points`}
              >
                <Mail size={12} strokeWidth={2} /> Draft email
              </button>
              {showFollowUp && (
                <button
                  onClick={async () => {
                    try {
                      await api.post("/company-outreach", {
                        company_id: company.id,
                        company_name: company.name || site,
                        company_domain: site,
                        domains: [...domains],
                      });
                    } catch {}
                    const url = `/mail?compose=1&company=${encodeURIComponent(company.name || site)}&domain=${encodeURIComponent(site)}&domains=${encodeURIComponent([...domains].join(","))}&company_id=${encodeURIComponent(company.id)}&follow_up=1&days_silent=${daysSinceSent}`;
                    window.location.href = url;
                  }}
                  className="cd-btn cd-btn--sm"
                  style={{ background: "#fef3c7", color: "#92400e", border: "1px solid #fbbf24" }}
                  data-testid={`follow-up-${group.key}`}
                  title={`Your last outreach was sent ${daysSinceSent} days ago with no reply. Draft a shorter, more direct follow-up.`}
                >
                  <Clock size={11} strokeWidth={2} /> Send follow-up · {daysSinceSent}d silent
                </button>
              )}
              {outreachSummary && <OutreachChip summary={outreachSummary} companyId={company.id} />}
            </div>
          ) : (
            <span className="text-[10px] text-[#98a2b3] italic">no company</span>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} style={{ background: "#fafbfc", padding: 0 }}>
            {company && <OutreachHeatmap companyId={company.id} companyName={company.name} />}
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th style={{ ...th, background: "#f2f4f7" }}>Session · Location</th>
                  <th style={{ ...th, background: "#f2f4f7" }}>Domains viewed</th>
                  <th style={{ ...th, background: "#f2f4f7" }}>Views</th>
                  <th style={{ ...th, background: "#f2f4f7" }}>First seen</th>
                  <th style={{ ...th, background: "#f2f4f7" }}>Last seen</th>
                  <th style={{ ...th, background: "#f2f4f7" }}>Email</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((r) => <HotLeadRow key={r.session_id} r={r} />)}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

function BullseyeCompaniesSection() {
  const [state, setState] = useState("loading"); // loading | ok | not_configured | error
  const [data, setData] = useState({ items: [], message: "" });

  const load = () => {
    setState("loading");
    api.get("/hot-leads/bullseye-companies", { params: { limit: 25 } })
      .then((r) => {
        const d = r.data || {};
        if (d.ok) {
          setData({ items: d.items || [], message: "" });
          setState("ok");
        } else if (d.reason === "not_configured" || d.reason === "missing_org_id") {
          setData({ items: [], message: d.message || "" });
          setState("not_configured");
        } else {
          setData({ items: [], message: d.message || "Bullseye request failed." });
          setState("error");
        }
      })
      .catch(() => {
        setData({ items: [], message: "Network error while fetching Bullseye companies." });
        setState("error");
      });
  };

  useEffect(() => { load(); }, []);

  return (
    <section className="mt-10" data-testid="bullseye-companies-section">
      <header className="mb-4 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline flex items-center gap-2" style={{ color: "#0369a1" }}>
            <Building2 className="w-3.5 h-3.5" strokeWidth={2} /> Identified companies · via Bullseye
          </div>
          <h2 className="font-display text-2xl tracking-tight mt-2">
            Which companies are eyeing your inventory.
          </h2>
          <p className="text-sm text-[#475467] mt-2 max-w-xl">
            U.S. company visits identified by Bullseye.so. Pair with the sessions above to know exactly who to send that cold email to.
          </p>
        </div>
        <button
          onClick={load}
          className="cd-btn cd-btn--outline cd-btn--sm"
          data-testid="bullseye-refresh"
        >
          <RefreshCcw size={13} strokeWidth={2} /> Refresh
        </button>
      </header>

      {state === "loading" ? (
        <div className="grid-panel p-8 text-center text-sm text-[#98a2b3]">Fetching from Bullseye…</div>
      ) : state === "not_configured" ? (
        <div className="grid-panel p-8 text-center" data-testid="bullseye-not-configured">
          <Building2 className="w-7 h-7 mx-auto text-[#98a2b3] mb-3" strokeWidth={1.5} />
          <div className="font-display text-lg mb-2">Bullseye is not connected.</div>
          <p className="text-sm text-[#475467] max-w-md mx-auto">
            {data.message || "Add your Bullseye org API key + organization ID in Settings → Analytics."}
          </p>
          <Link to="/settings" className="cd-btn cd-btn--outline cd-btn--sm mt-4 inline-flex">
            Open settings
          </Link>
        </div>
      ) : state === "error" ? (
        <div className="grid-panel p-6" data-testid="bullseye-error">
          <div className="text-sm" style={{ color: "var(--cd-danger)" }}>
            {data.message}
          </div>
        </div>
      ) : data.items.length === 0 ? (
        <div className="grid-panel p-8 text-center" data-testid="bullseye-empty">
          <Building2 className="w-7 h-7 mx-auto text-[#98a2b3] mb-3" strokeWidth={1.5} />
          <div className="font-display text-lg mb-2">No identified companies yet.</div>
          <p className="text-sm text-[#475467] max-w-md mx-auto">
            Bullseye will start populating this list a few minutes after the pixel is installed on your public pages.
          </p>
        </div>
      ) : (
        <div className="grid-panel">
          <table className="w-full text-sm" data-testid="bullseye-companies-table">
            <thead>
              <tr className="text-left">
                <th style={th}>Company</th>
                <th style={th}>Industry</th>
                <th style={th}>Size</th>
                <th style={th}>Location</th>
                <th style={th}>Visits</th>
                <th style={th}>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((c) => <BullseyeRow key={c.id || c.name} c={c} />)}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function BullseyeRow({ c }) {
  const site = (c.domain || "").replace(/^https?:\/\//i, "").replace(/\/$/, "");
  const location = [c.city, c.country].filter(Boolean).join(", ");
  return (
    <tr className="border-t border-[#eaecf0] hover:bg-[#fafafa]">
      <td style={td}>
        <div className="flex items-center gap-3">
          {c.logo_url ? (
            <img
              src={c.logo_url}
              alt=""
              className="w-8 h-8 rounded object-contain bg-white border border-[#eaecf0]"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="w-8 h-8 rounded bg-[#f2f4f7] flex items-center justify-center text-[10px] font-semibold text-[#475467]">
              {(c.name || "?").slice(0, 2).toUpperCase()}
            </div>
          )}
          <div>
            <div className="font-medium text-[#101828]">
              {c.name || site || "Unknown"}
              {c.icp && (
                <span
                  className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold"
                  style={{ background: "#ecfdf5", color: "#065f46" }}
                  title="Bullseye flagged this company as an Ideal Customer Profile match."
                >
                  ICP
                </span>
              )}
            </div>
            {site && (
              <a
                href={`https://${site}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-[#0369a1] hover:underline flex items-center gap-1"
              >
                <Globe size={11} strokeWidth={2} /> {site}
              </a>
            )}
          </div>
        </div>
      </td>
      <td style={td} className="text-[#475467]">{c.industry || "—"}</td>
      <td style={td} className="text-[#475467]">{c.size || "—"}</td>
      <td style={td} className="text-[#475467]">{location || "—"}</td>
      <td style={td}>
        <span className="font-semibold text-[#101828]">{c.visits || 0}</span>
      </td>
      <td style={td} className="text-[#475467]">{c.last_seen ? timeAgo(c.last_seen) : "—"}</td>
    </tr>
  );
}



const SENTIMENT_META = {
  interested:      { label: "interested",     bg: "#dcfce7", fg: "#166534", dot: "#16a34a" },
  negotiating:     { label: "negotiating",    bg: "#dbeafe", fg: "#1e40af", dot: "#2563eb" },
  not_interested:  { label: "not interested", bg: "#f3f4f6", fg: "#4b5563", dot: "#6b7280" },
  unsubscribe:     { label: "unsubscribed",   bg: "#fee2e2", fg: "#991b1b", dot: "#dc2626" },
  unknown:         { label: "unclassified",   bg: "#fef3c7", fg: "#92400e", dot: "#d97706" },
};

/**
 * Compact chip showing outreach state for a company. Renders as
 * "2 drafted · 1 sent · 1 reply" with a color-coded pill that shifts:
 *   - amber when only drafts exist
 *   - blue when at least one is marked sent
 *   - green when at least one reply is recorded
 * Clicking opens a mini history panel with per-attempt state + a "Mark as
 * sent/replied" quick action so the broker can log outcomes without
 * leaving Hot Leads (Mailgun webhook auto-updates come later).
 */
function OutreachChip({ summary, companyId }) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);

  const sentimentMeta = SENTIMENT_META[summary.lastSentiment] || null;
  const bg = summary.replyCount > 0
    ? (sentimentMeta ? sentimentMeta.bg : "#ecfdf5")
    : summary.sent > 0
      ? "#e0f2fe"
      : "#fef3c7";
  const fg = summary.replyCount > 0
    ? (sentimentMeta ? sentimentMeta.fg : "#065f46")
    : summary.sent > 0
      ? "#075985"
      : "#92400e";

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !history) {
      setLoading(true);
      try {
        const { data } = await api.get(`/company-outreach/${encodeURIComponent(companyId)}`);
        setHistory(data.items || []);
      } catch {
        setHistory([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const mark = async (id, status) => {
    try {
      await api.patch(`/company-outreach/${id}`, { status });
      const { data } = await api.get(`/company-outreach/${encodeURIComponent(companyId)}`);
      setHistory(data.items || []);
    } catch {}
  };

  return (
    <div className="relative">
      <button
        onClick={toggle}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-semibold"
        style={{ background: bg, color: fg }}
        data-testid={`outreach-chip-${companyId}`}
      >
        {summary.total} drafted
        {summary.sent > 0 && ` · ${summary.sent} sent`}
        {summary.replyCount > 0 && ` · ${summary.replyCount} ${summary.replyCount === 1 ? "reply" : "replies"}`}
        {sentimentMeta && ` · ${sentimentMeta.label}`}
      </button>
      {open && (
        <div
          className="absolute z-20 top-full mt-1 left-0 w-80 bg-white border border-[#eaecf0] rounded-lg shadow-lg p-3"
          data-testid={`outreach-history-${companyId}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="overline mb-2 flex justify-between items-center">
            <span>Outreach history</span>
            <button onClick={() => setOpen(false)} className="p-0.5 hover:bg-[#f4f4f5] rounded">
              <ChevronDown size={12} strokeWidth={2} className="rotate-180" />
            </button>
          </div>
          {loading ? (
            <div className="text-xs text-[#98a2b3] py-3 text-center">Loading…</div>
          ) : !history || history.length === 0 ? (
            <div className="text-xs text-[#98a2b3] py-3 text-center">No outreach yet.</div>
          ) : (
            <ul className="space-y-2 max-h-64 overflow-y-auto">
              {history.map((o) => (
                <li key={o.id} className="border border-[#eaecf0] rounded-md p-2" data-testid={`outreach-item-${o.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
                      style={
                        o.status === "replied"
                          ? { background: "#ecfdf5", color: "#065f46" }
                          : o.status === "sent"
                            ? { background: "#e0f2fe", color: "#075985" }
                            : { background: "#fef3c7", color: "#92400e" }
                      }
                    >
                      {o.status}
                    </span>
                    <span className="text-[10px] text-[#98a2b3]">
                      {o.created_at ? timeAgo(o.created_at) : ""}
                    </span>
                  </div>
                  {o.domains && o.domains.length > 0 && (
                    <div className="text-[11px] text-[#475467] mt-1">
                      Re: {o.domains.slice(0, 3).join(", ")}{o.domains.length > 3 ? `, +${o.domains.length - 3}` : ""}
                    </div>
                  )}
                  {o.reply_count > 0 && (
                    <div className="text-[10px] mt-1 flex items-center gap-1.5" style={{ color: "#065f46" }}>
                      <span>{o.reply_count} {o.reply_count === 1 ? "reply" : "replies"}{o.last_reply_at ? ` · last ${timeAgo(o.last_reply_at)}` : ""}</span>
                      {o.last_reply_sentiment && SENTIMENT_META[o.last_reply_sentiment] && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider"
                          style={{
                            background: SENTIMENT_META[o.last_reply_sentiment].bg,
                            color: SENTIMENT_META[o.last_reply_sentiment].fg,
                          }}
                          data-testid={`sentiment-${o.id}`}
                        >
                          <span
                            style={{
                              width: 6, height: 6, borderRadius: "50%",
                              background: SENTIMENT_META[o.last_reply_sentiment].dot,
                            }}
                          />
                          {SENTIMENT_META[o.last_reply_sentiment].label}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex gap-1 mt-2">
                    {o.status === "drafted" && (
                      <button
                        onClick={() => mark(o.id, "sent")}
                        className="text-[10px] px-2 py-1 rounded hover:bg-[#e0f2fe]"
                        style={{ color: "#075985" }}
                        data-testid={`mark-sent-${o.id}`}
                      >
                        Mark sent
                      </button>
                    )}
                    {o.status !== "replied" && (
                      <button
                        onClick={() => mark(o.id, "replied")}
                        className="text-[10px] px-2 py-1 rounded hover:bg-[#ecfdf5]"
                        style={{ color: "#065f46" }}
                        data-testid={`mark-replied-${o.id}`}
                      >
                        Mark replied
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}



/**
 * 30-day outreach cadence heatmap for one company. Renders as a compact
 * horizontal strip where each cell = one day, colored by activity:
 *   - Blue tint intensity ∝ number of outreach attempts sent that day
 *   - Green ring around the cell when the company replied that day
 *   - Empty gray cell for silent days
 * Read as: "when I reached out" (blue fill) vs "when they came back"
 * (green ring), across the last 30 days at a glance.
 */
function OutreachHeatmap({ companyId, companyName }) {
  const [state, setState] = useState({ loading: true, strip: [], totals: { sent: 0, replied: 0 } });

  useEffect(() => {
    let mounted = true;
    api.get(`/company-outreach/${encodeURIComponent(companyId)}/heatmap`, { params: { days: 30 } })
      .then((r) => { if (mounted) setState({ loading: false, ...r.data }); })
      .catch(() => { if (mounted) setState({ loading: false, strip: [], totals: { sent: 0, replied: 0 } }); });
    return () => { mounted = false; };
  }, [companyId]);

  const maxSent = Math.max(1, ...state.strip.map((d) => d.sent));

  return (
    <div
      className="border-b border-[#eaecf0] px-6 py-4"
      style={{ background: "#f9fafb" }}
      data-testid={`outreach-heatmap-${companyId}`}
    >
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="overline">Outreach cadence · last 30 days</div>
          <div className="text-[11px] text-[#475467] mt-1">
            {state.loading
              ? "Loading…"
              : state.totals.sent === 0 && state.totals.replied === 0
                ? `No outreach to ${companyName || "this company"} in the last 30 days.`
                : `${state.totals.sent} attempt${state.totals.sent === 1 ? "" : "s"} sent · ${state.totals.replied} repl${state.totals.replied === 1 ? "y" : "ies"} received`}
          </div>
        </div>
        <div className="flex gap-3 text-[10px] text-[#98a2b3]">
          <div className="flex items-center gap-1.5">
            <span style={{ width: 10, height: 10, background: "var(--cd-blue-500, #2563eb)", opacity: 0.7, borderRadius: 2, display: "inline-block" }} />
            Sent
          </div>
          <div className="flex items-center gap-1.5">
            <span style={{ width: 10, height: 10, background: "#f2f4f7", border: "2px solid #10b981", borderRadius: 2, display: "inline-block" }} />
            Replied
          </div>
        </div>
      </div>
      {!state.loading && state.strip.length > 0 && (
        <div className="flex gap-[3px] items-center">
          {state.strip.map((cell) => {
            const intensity = cell.sent > 0 ? 0.28 + Math.min(0.72, (cell.sent / maxSent) * 0.72) : 0;
            const bg = cell.sent > 0
              ? `rgba(37, 99, 235, ${intensity.toFixed(2)})`
              : "#f2f4f7";
            const ring = cell.replied > 0 ? "2px solid #10b981" : "1px solid #eaecf0";
            return (
              <div
                key={cell.date}
                title={`${cell.date}${cell.sent ? ` · ${cell.sent} sent` : ""}${cell.replied ? ` · ${cell.replied} replied` : ""}${!cell.sent && !cell.replied ? " · no activity" : ""}`}
                data-testid={`heatmap-cell-${cell.date}`}
                style={{
                  width: 18,
                  height: 26,
                  background: bg,
                  border: ring,
                  borderRadius: 3,
                  boxSizing: "border-box",
                }}
              />
            );
          })}
          <div className="text-[9px] text-[#98a2b3] ml-3 font-mono">
            {state.strip[0]?.date.slice(5)} → today
          </div>
        </div>
      )}
    </div>
  );
}

